<template>
  <div>
    <LogoBackgroundRemoval 
      ref="logo-background-removal"
      v-if="logoUrl && logoId"
      :logoUrl="logoUrl"
      :logoId="logoId"
      :hyperparameters="hyperparameters"
      @blobLoaded="onBlobLoaded"
      @canvasmounted="initialProcessImage"
    />

    <ModalBase show-modal @close-modal="close" narrow class="logo-generator-modal">
      <template v-slot:titlebar>
        <TitleBar variant-class="title-modal title-modal--simple">
          <template v-slot:pretitle>
            {{ $gettext('AI powered') }}
          </template>
          <template v-slot:title>
            {{ $gettext('Edit your logo') }}
          </template>
        </TitleBar>
      </template>
      <template v-slot:maincontent>
        <div class="modal-panel">
          <div class="modal-panel--top" >
            <LoadingSpinner v-if="loading" />
            <div class="resultswrap">
              <transition name="pop" appear mode="out-in">
                <div class="logo-img-wrap">
                  <img :src="logoUrl" :alt="$gettext('AI Generated Logo')" class="logo-img" :class="logoBorder">
                </div>
              </transition>
            </div>
          </div>

          <div class="modal-panel--bottom">

            <b-form-group label-class="gk-label-margin gk-modal-label" style="width: 50%;">
              <template slot="label">
                <span v-translate>Background</span>
              </template>
              <div style="display: flex; flex-direction: column; align-items: flex-start;">
                <div style="display: flex; flex-direction: row; margin-bottom: 0.5rem;">
                  <b-form-checkbox v-model="isBackgroundRemoved" @change="processImage" />
                  <span v-translate>Remove</span>
                </div>
                <GekkoButton
                  @click="recalculate"
                  :disabled="loading || !isBackgroundRemoved" 
                  :text="$gettext('Recalculate')"
                  icon="fa-solid fa-arrows-rotate"
                  variant="link"
                  class="p-0"
                />
              </div>
            </b-form-group>

            <b-form-group label-class="gk-label-margin gk-modal-label" style="width: 50%;">
              <template slot="label">
                <span v-translate>Size</span>
              </template>
              <GekkoButton 
                @click="zoomOut"
                :text="'-'"
                :disabled="loading" 
                style="margin-right: 5px;"
              />
              <GekkoButton 
                @click="zoomIn"
                :text="'+'"
                :disabled="loading" 
                style="margin-left: 5px;"
              />
            </b-form-group>

          </div>
        </div>
      </template>
      <template v-slot:actions>
        <div class="d-flex" style="gap: 1rem; justify-content: space-between;">
          <GekkoButton 
            @click="undo"
            :text="$gettext('Undo changes')"
            variant="outline-primary"
            icon="fa-regular fa-clock-rotate-left"
          />
          <GekkoButton 
            @click="save"
            :text="(isBrand) ? $gettext('Set logo') : $gettext('Set as company logo')"
            icon="fa-solid fa-check"
          />
        </div>
      </template>
    </ModalBase>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ModalBase from '@/components/base_components/ModalBase'
import GekkoButton from '@/components/GekkoButton'
import LogoBackgroundRemoval from '@/components/logo_generator/LogoBackgroundRemoval.vue'
import CompanyService from '@/store/services/company.service.js'
import TitleBar from '@/components/TitleBar'
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
  name: 'EditLogoModal',
  components: { 
    ModalBase,
    GekkoButton,
    LogoBackgroundRemoval,
    TitleBar,
    LoadingSpinner
  },
  props: {
    originalLogoUrl: {
      type: String,
      default: () => ''
    },
    logoId: {
      type: Number,
      default: () => null
    },
    isBrand: {
      type: Boolean,
      default: false
    },
    brandId: {
      type: [Number, String],
      default: null
    },
  },
  data () {
    return {
      loading: true,
      hyperparameters: {
        kComponentsBackground: 4,
        kComponentsOutliers: 2,
        ratioThreshold: 0.08,
        blurPixelRate: 1,
        redDiff: 0,
        blueDiff: 0,
        greenDiff: 0
      },
      isBackgroundRemoved: false,
      logoUrl: null,
      logoBlob: null
    }
  },
  computed: {
    ...mapGetters ({
      currentCompany: 'main/getCurrentCompany'
    }),
    logoBorder() {
      if (this.isBackgroundRemoved) return 'logo-border-background-removed'
      else return ''
    }
  },
  created () {
    this.logoUrl = this.originalLogoUrl
  },
  mounted () {
    // this.$refs['logo-background-removal'].processImage(this.logoId, this.isBackgroundRemoved)
  },
  methods: {
    close () {
      this.$emit('close', false)
      document.body.classList.remove('modal-on')
    },
    initialProcessImage () {
      console.log('initialProcessImage')
      this.$refs['logo-background-removal'].processImage(this.logoId, this.isBackgroundRemoved)
    },
    onBlobLoaded (blob) {
      console.log('onBlobLoaded')
      this.logoBlob = blob
      this.logoUrl = URL.createObjectURL(blob)
      this.loading = false
    },
    undo () {
      this.loading = true
      this.isBackgroundRemoved = false
      this.hyperparameters = {
        kComponentsBackground: 4,
        kComponentsOutliers: 2,
        ratioThreshold: 0.08,
        blurPixelRate: 1,
        redDiff: 0,
        blueDiff: 0,
        greenDiff: 0
      }
      this.$refs['logo-background-removal'].processImage(this.logoId, this.isBackgroundRemoved)
    },
    async save () {
      this.loading = true

      const response = await fetch(this.logoUrl)
      
      let file = null
      if (this.logoBlob !== null) {
        file = new File([this.logoBlob], 'logo_image.png', { type: this.logoBlob.type });
      } else {
        const blob = await response.blob();
        file = new File([blob], 'logo_image.jpg', { type: blob.type });
      }

      let companyData = {
        id: this.currentCompany.id,
        name: this.currentCompany.name,
        country: this.currentCompany.country,
        logo: file
      }
      if (!this.isBrand) {
        CompanyService.updateLogo(this.currentCompany.id, companyData).then((response) => {
          this.$emit('logoSetToCompany', response.data)
          this.close()
          this.loading = false
        }).catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: this.$gettext('We could not update your company logo'),
            variant: "danger",
            duration: 4
          })
        })
      } else {
        this.$emit('logoSetToBrand', file)
      }
      
    },
    zoomIn () {
      this.hyperparameters.ratioThreshold = this.hyperparameters.ratioThreshold + 0.1
      this.processImage()
    },
    zoomOut () {
      this.hyperparameters.ratioThreshold = this.hyperparameters.ratioThreshold - 0.1
      this.processImage()
    },
    processImage () {
      this.loading = true
      this.$refs['logo-background-removal'].processImage(this.logoId, this.isBackgroundRemoved)
    },
    getRandomInt(min, max) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    recalculate() {
      this.hyperparameters['kComponentsBackground'] = this.getRandomInt(3, 8),
      this.hyperparameters['kComponentsOutliers'] = this.getRandomInt(3, 8),
      this.hyperparameters['blurPixelRate'] = this.getRandomInt(0, 2)
      this.processImage()
    }
  }
}
</script>

<style lang="scss">
.logo-generator-modal {
  .gk-modal {
    min-height: 600px;
    max-height: 900px;
  }
}
</style>

<style lang="scss" scoped>
.logo-generator-modal {
  .modal-panel {
    display: flex;
    width: 100%;
    height: 100%;

    flex-direction: column;

    &--title {
      .titlewrap {
        .titlebar.title-modal {
          margin-bottom: 0;
          .action-bar { display: none; }
        }
      }
    }

    &--top {
      background: white;
      transition: background-color 0.7s ease;
      height: 100%;
      width: 100%;
      overflow: hidden;
      position: relative;
      .titlewrap {
        .titlebar.title-modal {
          margin-bottom: 0;
          .action-bar { display: none; }
        }
      }
      .resultswrap {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        position: relative;
        background: white;
        transition: background-color 0.7s ease;
      }
      &.initiated {
        .resultswrap {
          background: #dddddd;
        }
      }
    }
    &--selectedlogo {
      padding: 0.5rem 1rem;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      h3 { font-size: 20px; }
      background: $chatbotPanelBg;
      position: relative;
      span {
        font-style: italic;
        color: $colorTextSemi;
      }
    }
    &--bottom {
      font-size: 1rem;
      display: flex;
      // flex-direction: column;
      gap: 2rem;
      background: white;
      height: 100%;
      width: 100%;
      flex: 1;
      .prompt-wrap {
        padding: 1rem;
        padding-top: 0.5rem;
        border-top: 1px solid $colorBorderDash;
        &__options {
          display: flex;
          gap: 0.5rem;
        }
        .form-group {
          margin-bottom: 0.5rem;
        }
        .custom-select {
          background-color: white;
        }
      }
    }
  }
}

.logo-img-wrap {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 2rem;
  .logo-img {
    height: 100%;
    width: 100%;
    object-fit: contain;
    // filter: drop-shadow(2px 2px 10px rgba(0, 0, 0, 0.5));
  }
}

.logo-border-background-removed {
  border: solid 2px $inputBorderColor;
  border-radius: 12px;

  background: conic-gradient(#ffffff 90deg, #eeeeee 90deg 180deg, #ffffff 180deg 270deg, #eeeeee 270deg);
  background-repeat: repeat;
  background-size: 30px 30px;
  background-position: top left;
}
</style>
